import { useCallback, useContext } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { AccountContext } from '@/contexts/account'
import type { AccountContextValue } from '@/contexts/account'
import { guardScreenEditOrAlert, isScreenReadOnlyForUi } from '@/libs/hotel-pms-permission'

/**
 * 売上画面向けの read-only 判定と編集ガード。
 */
export const useSalesEditGuard = () => {
  const { t } = useTranslation()
  const { permissions, permissionsLoaded, permissionsError } = useContext<AccountContextValue>(AccountContext)
  const isSalesReadOnly = isScreenReadOnlyForUi(permissions, 'sales', permissionsLoaded, permissionsError)

  const guardSalesEdit = useCallback(() => {
    return guardScreenEditOrAlert(permissions, 'sales', permissionsLoaded, permissionsError, t)
  }, [permissions, permissionsLoaded, permissionsError, t])

  const createGuardedSetEditMode = useCallback(
    (setIsEditMode: Dispatch<SetStateAction<boolean>>): Dispatch<SetStateAction<boolean>> =>
      next => {
        setIsEditMode(prev => {
          const resolved = typeof next === 'function' ? next(prev) : next
          if (resolved && !guardScreenEditOrAlert(permissions, 'sales', permissionsLoaded, permissionsError, t)) {
            return prev
          }
          return resolved
        })
      },
    [permissions, permissionsLoaded, permissionsError, t],
  )

  return { isSalesReadOnly, guardSalesEdit, createGuardedSetEditMode }
}
